import { FunctionComponent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { LoadingBlock } from "@/components/Spinner/LoadingBlock";
import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { useGetDynamicsSyncLogsQuery } from "@/pages/Private/redux/dynamics/dynamics.api";

import { MsdynamicsEntityType, MsdynamicsSyncLog } from "../schema/dynamics";

const LIMIT = 20;

const formatDate = (dateStr: string): string =>
	new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});

/**
 * Semantic tokens only (apps/web/CLAUDE.md §7). The first version used raw Tailwind palette
 * classes (`bg-red-100 text-red-800`, `bg-yellow-100`, …), and `tailwind.config.js` defines
 * **none** of those shades — so every badge except SUCCESS rendered with no background at all.
 * A FAILED row looked identical to plain text, which the ticket-8 screenshots caught.
 */
const STATUS_COLORS: Record<string, string> = {
	SUCCESS: "bg-status-success-light text-status-success",
	PARTIAL: "bg-status-warning-light text-status-wrong-person",
	FAILED: "bg-status-error-light text-status-error",
	PENDING: "bg-status-info-light text-status-info",
	IN_PROGRESS: "bg-status-running-light text-status-running",
};

const StatusBadge: FunctionComponent<{ status: string }> = ({ status }) => (
	<span
		className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] ?? "bg-gray-100 text-gray-800"}`}
	>
		{status}
	</span>
);

/**
 * The first error message of a log row. `MsdynamicsSyncLogDTO` carries an `errors` array of
 * free-form objects — there is no flat `errorMessage` column (the Pipedrive tab read one that
 * never existed, so its Error column was always empty until PREC-4428).
 *
 * The key is **`error`**: every writer (`MsdynamicsSyncService.failSync`,
 * `MsdynamicsDealSyncService`) stores `{ prospectId, error }`. Reading `message` — which this
 * did until the ticket-8 dry run rendered a real failed row — made the Error column show "—"
 * for every failure the customer could actually have acted on. `message` is kept as a fallback
 * because the column is typed `Record<string, unknown>` and nothing stops a future writer from
 * using it.
 */
const firstErrorMessage = (log: MsdynamicsSyncLog): string => {
	const entry = log.errors?.[0];
	// A PARTIAL row carries `warning` instead: the record was written under another owner because
	// Dataverse refused the mapped one for a missing privilege (PREC-4361).
	const text = entry?.error ?? entry?.warning ?? entry?.message;

	return typeof text === "string" && text.length > 0 ? text : "—";
};

export const DynamicsSyncLogsTab: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.msdynamics.syncLogs.${key}`);

	const [entityFilter, setEntityFilter] = useState<string | undefined>(undefined);
	const [offset, setOffset] = useState(0);

	const { data, isLoading } = useGetDynamicsSyncLogsQuery({
		entityType: entityFilter,
		limit: LIMIT,
		offset,
	});

	const entityOptions: DropdownAutoCompleteItem[] = useMemo(
		() => [
			{ id: "ALL", title: ts("allEntities") },
			{ id: MsdynamicsEntityType.CONTACT, title: "Contact" },
			{ id: MsdynamicsEntityType.ACCOUNT, title: "Account" },
			{ id: MsdynamicsEntityType.LEAD, title: "Lead" },
			{ id: MsdynamicsEntityType.OPPORTUNITY, title: "Opportunity" },
			{ id: MsdynamicsEntityType.CAMPAIGN_RESPONSE, title: "Campaign response" },
			{ id: MsdynamicsEntityType.CAMPAIGN, title: "Campaign" },
			{ id: MsdynamicsEntityType.MARKETING_LIST, title: "Marketing list" },
		],
		[ts]
	);

	const handleEntityFilterChange = (value: DropdownAutoCompleteItem) => {
		setEntityFilter(value.id === "ALL" ? undefined : (value.id as string));
		setOffset(0);
	};

	if (isLoading) {
		// `Spinner` defaults to a white spinner, which was invisible on this white panel.
		return <LoadingBlock />;
	}

	const logs = data?.logs ?? [];
	const total = data?.total ?? 0;

	return (
		<div className="flex flex-col w-full gap-4">
			<div className="text-sm text-text-meta">{ts("description")}</div>

			<div className="w-full max-w-[250px]">
				<Dropdown
					data={entityOptions}
					defaultValue={entityOptions[0]}
					floating={true}
					handleSelect={handleEntityFilterChange}
					label={ts("filterByEntity")}
				/>
			</div>

			{logs.length === 0 ? (
				<div className="text-center py-12 text-text-meta text-sm">{ts("noLogs")}</div>
			) : (
				<div className="rounded-xl border border-border-light bg-brand-white overflow-x-auto">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-border-light bg-gray-50">
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("entityType")}
								</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("status")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("synced")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("failed")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">{ts("error")}</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("startedAt")}
								</th>
								<th className="px-4 py-3 text-left font-medium text-text-meta">
									{ts("completedAt")}
								</th>
							</tr>
						</thead>
						<tbody>
							{logs.map((log) => (
								<tr key={log.id} className="border-b border-border-light last:border-b-0">
									<td className="px-4 py-3 text-text">{log.entityType}</td>
									<td className="px-4 py-3">
										<StatusBadge status={log.status} />
									</td>
									<td className="px-4 py-3 text-text">{log.recordsSynced}</td>
									<td className="px-4 py-3 text-text">{log.recordsFailed}</td>
									<td
										className="px-4 py-3 text-text-meta text-xs max-w-[200px] truncate"
										title={firstErrorMessage(log)}
									>
										{firstErrorMessage(log)}
									</td>
									<td className="px-4 py-3 text-text-meta">{formatDate(log.createdAt)}</td>
									<td className="px-4 py-3 text-text-meta">
										{log.completedAt ? formatDate(log.completedAt) : "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{total > LIMIT && (
				<div className="flex justify-center gap-2 mt-2">
					<button
						className="px-3 py-1 text-sm rounded border border-border-light cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						disabled={offset === 0}
						type="button"
						onClick={() => setOffset((prev) => Math.max(0, prev - LIMIT))}
					>
						{t("basics.previous")}
					</button>
					<button
						className="px-3 py-1 text-sm rounded border border-border-light cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
						disabled={offset + LIMIT >= total}
						type="button"
						onClick={() => setOffset((prev) => prev + LIMIT)}
					>
						{t("basics.next")}
					</button>
				</div>
			)}
		</div>
	);
};
